import React from "react";
import _ from "lodash";
import classNames from "classnames";
import { connect } from "react-redux";

import { useI18n } from "Hooks";
import { formatDate, onEnterOrSpaceKeyDown } from "Utils";
import { colors } from "@toddle-edu/ds-theme";
import { compose, graphql } from "lib/apolloClient";
import AnimateHeight from "UIComponents/AnimateHeight";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import { Button, ProgressIndicator, Tooltip } from "@toddle-edu/ds-web";
import {
  ChevronDownOutlined,
  ExpandArrowsTwoOutlined,
  TickCircleFilled,
  WarningCircleFilled,
} from "@toddle-edu/ds-icons";

import classes from "./WorkflowItem.scss";
import { STEPS } from "AppComponents/ManageWorkflowsModal/modules/Utils";
import { WORKFLOW_CREATED_AT_FORMAT } from "Workflows/modules/Constants";
import { getWorkflowProgressDataQuery } from "Workflows/modules/WorkflowsQueries";
import { useManageWorkflowModal } from "AppComponents/ManageWorkflowsModal/modules/ManageWorkflowModalContext";

const TitleWithTooltip = ({ title, children }) => (
  <Tooltip tooltip={title} placement="top" zIndex={999999}>
    {children}
  </Tooltip>
);

const getStatusSummary = ({ stepType, stepCountByType, groupedSteps }) => {
  const expectedCount = stepCountByType[stepType];
  const currentSteps = groupedSteps[stepType] || [];

  const successCount = _.filter(currentSteps, { status: "COMPLETED" }).length;
  const failedCount = _.filter(currentSteps, { status: "FAILED" }).length;

  if (_.isNil(expectedCount)) {
    return {
      status: "PROCESSING",
      successCount: 0,
      failedCount: 0,
      totalCount: 0,
    };
  }

  if (expectedCount === 0) {
    return {
      status: "COMPLETED",
      successCount: 0,
      failedCount: 0,
      totalCount: 0,
    };
  }

  if (currentSteps.length < expectedCount) {
    return {
      status: "PROCESSING",
      successCount,
      failedCount,
      totalCount: expectedCount,
    };
  }

  if (failedCount > 0) {
    return {
      status: "FAILED",
      successCount,
      failedCount,
      totalCount: expectedCount,
    };
  }

  if (successCount === expectedCount) {
    return {
      status: "COMPLETED",
      successCount,
      failedCount,
      totalCount: expectedCount,
    };
  }

  return {
    status: "PROCESSING",
    successCount,
    failedCount,
    totalCount: expectedCount,
  };
};

const getStatusConfigs = ({
  status,
  successCount,
  failedCount,
  totalCount,
  t,
}) => {
  if (status === "PROCESSING") {
    return {
      icon: null,
      label: t("common:processing_no_dots"),
      textColor: "--text-semantic-success",
    };
  }

  if (totalCount === 0) {
    return {
      icon: <TickCircleFilled size="xx-small" variant="success" />,
      label: t("common:skipped"),
    };
  }

  if (failedCount === totalCount) {
    return {
      icon: <WarningCircleFilled size="xx-small" variant="critical" />,
      label: t("common:failed_with_count", { count: failedCount }),
    };
  }

  return {
    icon: (
      <TickCircleFilled
        size="xx-small"
        variant={successCount < totalCount ? "warning" : "success"}
      />
    ),
    label: t("common:count_of_total", {
      count: successCount,
      totalCount,
    }),
  };
};

const WorkflowSteps = ({
  t,
  expandedJobId,
  updateExpandedJobId,
  jobId,
  subtasks,
  totalCount,
  completedCount,
  handleRedirectClick,
  stepCountByType,
  groupedSteps,
}) => {
  const isExpanded = expandedJobId === jobId;
  const toggleIsExpanded = () => updateExpandedJobId(jobId);

  return (
    <div className={classes.stepsContainer}>
      <div
        className={classes.headerContainer}
        onClick={toggleIsExpanded}
        onKeyDown={e => onEnterOrSpaceKeyDown(e, toggleIsExpanded)}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={t("workflows:m_of_n_completed", {
          completedCount,
          totalCount,
        })}
      >
        <div className={classNames("text-body-s", classes.titleContainer)}>
          {t("workflows:m_of_n_completed", {
            completedCount,
            totalCount,
          })}
        </div>
        <div className={classes.rightSection}>
          <ExpandArrowsTwoOutlined
            variant="subtle"
            size="xxx-small"
            onClick={handleRedirectClick}
          />
          <ChevronDownOutlined
            variant="subtle"
            size="xxx-small"
            className={classNames(classes.arrowContainer, {
              [classes.openArrow]: isExpanded,
              [classes.closeArrow]: !isExpanded,
            })}
          />
        </div>
      </div>
      <AnimateHeight duration={300} height={!isExpanded ? 0 : "auto"}>
        <div className={classes.scrollContainer}>
          {subtasks.map(({ localeKey, stepType }) => {
            const summary = getStatusSummary({
              stepType,
              stepCountByType,
              groupedSteps,
            });
            const { icon: Icon, label, textColor } = getStatusConfigs({
              ...summary,
              t,
            });

            return (
              <div className={classes.workflowRow} key={localeKey}>
                <TitleWithTooltip title={t(`workflows:${localeKey}`)}>
                  <div
                    className={classNames(
                      "text-body-s",
                      classes.titleContainer
                    )}
                  >
                    {t(`workflows:${localeKey}`)}
                  </div>
                </TitleWithTooltip>
                <div className={classes.workflowStatus}>
                  <Button
                    dsVersion="2.0"
                    type="inline"
                    size="small"
                    style={{ color: textColor && `var(${textColor})` }}
                  >
                    <div className={classes.statusLabel}>
                      {Icon}
                      {label}
                    </div>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </AnimateHeight>
    </div>
  );
};

const WorkflowItem = ({
  item = {},
  expandedJobId,
  updateExpandedJobId,
  nodes,
  steps,
  stepCountByType,
}) => {
  const {
    setIsModalOpen,
    setCurrentStep,
    setActiveWorkflowId,
    setHeaderProps,
  } = useManageWorkflowModal();

  const { id, title, failedCount, createdAt, subTaskId } = item;
  const groupedSteps = _.groupBy(steps, "stepType");
  const sortedNodes = _.sortBy(nodes, "stepOrder");
  const totalCount = Object.keys(groupedSteps).length;
  const completedCount = Object.values(groupedSteps).filter(group =>
    group.some(step => step.status === "COMPLETED")
  ).length;

  const { t } = useI18n(["common", "workflows"]);

  const handleRedirectClick = e => {
    e.stopPropagation();
    setIsModalOpen(true);
    setCurrentStep(STEPS.STEP_2);
    setActiveWorkflowId(subTaskId);
    setHeaderProps({
      [STEPS.STEP_2]: {
        title: formatDate({
          date: createdAt,
          format: WORKFLOW_CREATED_AT_FORMAT,
        }),
        subtitle: title,
      },
    });
  };

  return (
    <div className={classes.container} id="my_tooltip">
      <div className={classes.topContainer}>
        <TitleWithTooltip title={title}>
          <div className={classNames("text-label", classes.titleContainer)}>
            {title}
          </div>
        </TitleWithTooltip>
        <ProgressIndicator
          variant="progress-bar"
          showTooltipArrow
          containerStyle={{ width: "100%" }}
          segmentedProgress={[
            {
              id: "SUCCESS",
              color: colors.surfaceOnSuccess,
              progress: Math.round((completedCount * 100) / totalCount),
              tooltip: "Completed",
            },
            {
              id: "FAILURE",
              color: colors.surfaceOnError,
              progress: Math.round((failedCount * 100) / totalCount),
              tooltip: "Failed",
            },
          ]}
        />
      </div>
      <WorkflowSteps
        subtasks={sortedNodes}
        jobId={id}
        t={t}
        expandedJobId={expandedJobId}
        updateExpandedJobId={updateExpandedJobId}
        totalCount={totalCount}
        completedCount={completedCount}
        handleRedirectClick={handleRedirectClick}
        stepCountByType={stepCountByType}
        groupedSteps={groupedSteps}
      />
    </div>
  );
};

const mapStateToProps = (state, { item: { subTaskId } }) => {
  return {
    isData: true,
    isLoading: false,
    variables: { subTaskId },
  };
};

const ConnectedWorkflowItem = compose(
  connect(mapStateToProps, null),
  graphql(getWorkflowProgressDataQuery, {
    name: "getWorkflowProgressData",
    options: ({ variables }) => ({
      fetchPolicy: "cache-and-network",
      variables,
    }),
    props: ({ getWorkflowProgressData, ownProps: { isData, isLoading } }) => {
      const subTasksNode = _.get(getWorkflowProgressData, "integration", {});
      const {
        nodes = [],
        stepCountByType = {},
        steps = [],
        subTaskData = {},
      } = _.get(subTasksNode, "fetchWorkflowProgressData", {});

      return {
        getWorkflowProgressData,
        nodes,
        stepCountByType,
        steps,
        subTaskData,
        isData: !_.isEmpty(subTasksNode) && isData,
        isLoading:
          getWorkflowProgressData["networkStatus"] === 1 ||
          getWorkflowProgressData["networkStatus"] === 2 ||
          isLoading,

        onPusherEventTriggered: () => {
          getWorkflowProgressData.refetch();
        },
      };
    },
  }),
  withPusherBind(["workflowProgressUpdate"])
)(WorkflowItem);

const WorkflowItemWrapper = props => {
  return <ConnectedWorkflowItem {...props} />;
};

export default WorkflowItemWrapper;
